
import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthResponse } from '../types/user';
import { APIHOST } from '../common';

interface LoginProps {
  onLoginSuccess: (authResponse: AuthResponse) => void;
  onCancel: () => void;
  onSwitchToRegister: () => void;
  onSwitchToReset?: () => void;
}

export default function Login({ onLoginSuccess, onCancel, onSwitchToRegister, onSwitchToReset }: LoginProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${APIHOST}/api/auth/local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: formData.email.trim().toLowerCase(),
          password: formData.password
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Login failed');
      }

      const authResponse: AuthResponse = await response.json();
      
      // Store the JWT token in localStorage
      localStorage.setItem('authToken', authResponse.jwt);
      localStorage.setItem('user', JSON.stringify(authResponse.user));
      console.debug('Login successful, user data stored in localStorage:', authResponse.user);
      onLoginSuccess(authResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.email.trim() && formData.password;

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Sign in to continue with your hotel registration.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.email}
          onChange={handleInputChange('email')}
          required
          disabled={loading}
        />

        <TextField
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange('password')}
          required
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}

        <Button
          onClick={handleSubmit}
          variant="contained"
          fullWidth
          disabled={!isFormValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ mt: 2 }}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>
        
        {onSwitchToReset && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button 
              onClick={onSwitchToReset}
              sx={{ textTransform: 'none', p: 0, textDecoration: 'underline' }}
              size="small"
              color="primary"
            >
              Forgot your password?
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Button 
            onClick={onSwitchToRegister}
            sx={{ textTransform: 'none', p: 0 }}
            size="small"
          >
            Create one here
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}